import { Dialog, DialogContent, DialogTitle, Box, Fade, CircularProgress, Tooltip, IconButton, Collapse, Divider, Paper, useMediaQuery, useTheme, Zoom } from "@mui/material";
import { PTButton, PTTypography, PTTranslate } from "../../components/ui";
import { PaymentsOutlined, SecurityOutlined, InfoOutlined, CloseOutlined, ExpandMore, ExpandLess, LogoutOutlined, ShoppingCartCheckoutOutlined } from "@mui/icons-material";
import { useState } from "react";
import { useAuth } from "../../provider";

const GlobalPaymentDialog = ({ 
  open, 
  onClose, 
  cmiPaymentRequest,
  disableEscapeKeyDown = false,
  disableBackdropClick = false,
  amount = 1200 
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const handleClose = (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    onClose();
  };

  const handlePaymentProceed = () => {
    setIsProcessing(true);
    if (cmiPaymentRequest) {
      redirectToCMI(cmiPaymentRequest);
    } else {
      console.error('Données de paiement CMI non disponibles');
      setIsProcessing(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const redirectToCMI = (cmiPaymentRequest) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = 'https://testpayment.cmi.co.ma/fim/est3Dgate';

    if (cmiPaymentRequest) {
      Object.entries(cmiPaymentRequest).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    }
  };

  const handleBackdropClick = (event) => {
    if (!disableBackdropClick) {
      onClose(event);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!disableBackdropClick ? handleClose : undefined}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={disableEscapeKeyDown}
      TransitionComponent={Zoom}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.15)'
          }
        }
      }}
    >
      <DialogTitle 
        sx={{ 
          background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
          py: 2,
          px: isMobile ? 2 : 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <PaymentsOutlined 
            sx={{ 
              color: '#f0fdfa', 
              fontSize: 22,
              animation: 'fadeIn 0.5s ease-out',
              '@keyframes fadeIn': {
                '0%': { opacity: 0, transform: 'scale(0.8)' },
                '100%': { opacity: 1, transform: 'scale(1)' }
              }
            }} 
          />
          <PTTypography 
            variant="h6" 
            sx={{ 
              color: '#f0fdfa', 
              fontWeight: 600,
              letterSpacing: '-0.01em',
              fontSize: isMobile ? '1.1rem' : '1.25rem',
              animation: 'slideIn 0.5s ease-out',
              '@keyframes slideIn': {
                '0%': { opacity: 0, transform: 'translateX(-10px)' },
                '100%': { opacity: 1, transform: 'translateX(0)' }
              }
            }}
          >
            <PTTranslate tk="paiement.confirmation.titre" />
          </PTTypography>
        </Box>
        {!disableBackdropClick && (
          <IconButton
            onClick={handleClose}
            size="small"
            sx={{
              color: '#f0fdfa',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ 
        p: isMobile ? 2 : 2.5,
        bgcolor: '#ffffff',
        '&:first-of-type': { pt: 2.5 }
      }}>
        <Box sx={{ mb: 3 }}>
          <PTTypography 
            variant="body2" 
            sx={{ 
              color: '#475569', 
              mb: 2.5,
              fontSize: isMobile ? '0.875rem' : '0.9375rem',
              lineHeight: 1.5,
              letterSpacing: '0.01em',
              animation: 'fadeInUp 0.6s ease-out',
              '@keyframes fadeInUp': {
                '0%': { opacity: 0, transform: 'translateY(10px)' },
                '100%': { opacity: 1, transform: 'translateY(0)' }
              }
            }}
          >
              <PTTranslate tk={disableBackdropClick ? "paiement.confirmation.description" : "paiement.confirmation.description.licence"} />
          </PTTypography>

          <Paper 
            elevation={0}
            sx={{
              p: 2.5,
              bgcolor: '#f8fafc',
              borderRadius: 1.5,
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#cbd5e1',
                bgcolor: '#f1f5f9'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <PTTypography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 500 }}>
                <PTTranslate tk={"paiement.confirmation.reference"} fallback="Référence" />
              </PTTypography>
              <PTTypography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
                {cmiPaymentRequest?.itemNumber1 || `SUB-${new Date().getFullYear()}-${String(Date.now()).substr(-5)}`}
              </PTTypography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <PTTypography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 500 }}>
                <PTTranslate tk="paiement.confirmation.montant" />
              </PTTypography>
              <PTTypography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
                {formatAmount(amount)} MAD
              </PTTypography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <PTTypography variant="subtitle2" sx={{ color: '#64748b', fontWeight: 500 }}>
                <PTTranslate tk="paiement.confirmation.type" />
              </PTTypography>
              <PTTypography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
                <PTTranslate tk={!disableBackdropClick ? "paiement.confirmation.serviceExport" : "paiement.confirmation.service"} />
              </PTTypography>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box 
            onClick={() => setShowDetails(!showDetails)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#0f766e',
                bgcolor: '#f0fdfa'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlined sx={{ color: '#0f766e' }} />
              <PTTypography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
                <PTTranslate tk="paiement.confirmation.details" />
              </PTTypography>
            </Box>
            {showDetails ? <ExpandLess sx={{ color: '#0f766e' }} /> : <ExpandMore sx={{ color: '#0f766e' }} />}
          </Box>

          <Collapse in={showDetails} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <PTTypography variant="body2" sx={{ color: '#475569' }}>
                <PTTranslate tk="paiement.confirmation.details.description" />
              </PTTypography>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Box 
            onClick={() => setShowSecurityInfo(!showSecurityInfo)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              p: 1.5,
              borderRadius: 2,
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: '#0f766e',
                bgcolor: '#f0fdfa'
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityOutlined sx={{ color: '#0f766e' }} />
              <PTTypography variant="body2" sx={{ color: '#0f172a', fontWeight: 500 }}>
                <PTTranslate tk="paiement.confirmation.securite" />
              </PTTypography>
            </Box>
            {showSecurityInfo ? <ExpandLess sx={{ color: '#0f766e' }} /> : <ExpandMore sx={{ color: '#0f766e' }} />}
          </Box>

          <Collapse in={showSecurityInfo} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <PTTypography variant="body2" sx={{ color: '#475569' }}>
                <PTTranslate tk="paiement.confirmation.securite.details" />
              </PTTypography>
            </Box>
          </Collapse>
        </Box>

        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          padding: isMobile ? '12px 16px' : '16px 20px',
          borderTop: '1px solid #e2e8f0',
          bgcolor: '#f8fafc',
          position: isMobile ? 'sticky' : 'relative',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1
        }}>
          {!disableBackdropClick && (
            <PTButton
              title="paiement.confirmation.annuler"
              onClick={handleClose}
              variant="outlined"
              disabled={isProcessing}
              sx={{ 
                minWidth: isMobile ? "90px" : "100px",
                height: "40px",
                borderRadius: 2,
                color: '#475569',
                borderColor: '#cbd5e1',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: '#94a3b8',
                  bgcolor: 'rgba(203, 213, 225, 0.1)',
                  transform: 'translateY(-1px)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  bgcolor: 'rgba(203, 213, 225, 0.2)'
                },
                '&:disabled': {
                  color: '#94a3b8',
                  borderColor: '#e2e8f0',
                  bgcolor: '#f8fafc'
                }
              }}
            />
          )}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            {disableBackdropClick && (
              <PTButton
                title="paiement.confirmation.deconnecter"
                onClick={handleLogout}
                variant="outlined"
                disabled={isProcessing}
                startIcon={<LogoutOutlined />}
                sx={{ 
                  minWidth: isMobile ? "120px" : "140px",
                  height: "40px",
                  borderRadius: 2,
                  color: '#dc2626',
                  borderColor: '#fecaca',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    borderColor: '#f87171',
                    bgcolor: 'rgba(220, 38, 38, 0.1)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0)',
                    bgcolor: 'rgba(220, 38, 38, 0.2)'
                  },
                  '&:disabled': {
                    color: '#94a3b8',
                    borderColor: '#e2e8f0',
                    bgcolor: '#f8fafc'
                  }
                }}
              />
            )}
            <PTButton
              title={disableBackdropClick ? "paiement.confirmation.obligatoire" : "paiement.confirmation.proceder"}
              onClick={handlePaymentProceed}
              variant="contained"
              disabled={isProcessing}
              sx={{ 
                minWidth: isMobile ? "140px" : "180px",
                height: "40px",
                borderRadius: 2,
                background: disableBackdropClick 
                  ? 'linear-gradient(45deg, #e11d48 30%, #be123c 90%)'
                  : 'linear-gradient(45deg, #0f766e 30%, #0d9488 90%)',
                color: 'white',
                textTransform: 'none',
                fontSize: '0.9375rem',
                fontWeight: 600,
                boxShadow: disableBackdropClick
                  ? '0 4px 20px 0 rgba(225, 29, 72, 0.2)'
                  : '0 4px 20px 0 rgba(15, 118, 110, 0.2)',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: disableBackdropClick
                    ? '0 6px 30px 0 rgba(225, 29, 72, 0.3)'
                    : '0 6px 30px 0 rgba(15, 118, 110, 0.3)',
                  background: disableBackdropClick 
                    ? 'linear-gradient(45deg, #be123c 30%, #9b2c2c 90%)'
                    : 'linear-gradient(45deg, #0d9488 30%, #115e59 90%)'
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: disableBackdropClick
                    ? '0 3px 15px 0 rgba(225, 29, 72, 0.25)'
                    : '0 3px 15px 0 rgba(15, 118, 110, 0.25)'
                },
                '&:disabled': {
                  background: '#e2e8f0',
                  color: '#94a3b8',
                  boxShadow: 'none',
                  transform: 'none'
                }
              }}
              startIcon={
                isProcessing ? (
                  <CircularProgress size={20} thickness={4} sx={{ color: 'inherit', animation: 'spin 1s linear infinite', '@keyframes spin': { '0%': { transform: 'rotate(0deg)' }, '100%': { transform: 'rotate(360deg)' } } }} />
                ) : (
                  <ShoppingCartCheckoutOutlined sx={{ fontSize: 20, animation: 'bounce 1s ease infinite', '@keyframes bounce': { '0%, 100%': { transform: 'translateY(0)' }, '50%': { transform: 'translateY(-2px)' } } }} />
                )
              }
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default GlobalPaymentDialog; 